/**
 * Is a `position: sticky` element currently pinned?
 *
 * A zero-height sentinel is rendered immediately above the sticky element; the
 * moment it scrolls out of the top of the viewport, the element below it is
 * stuck. One IntersectionObserver, no scroll handler — same reasoning as
 * usePhaseTheme.
 *
 * The countdown bar uses it to earn its space twice: a full panel at rest,
 * and a thin strip once it's riding the top of the screen.
 */

import { type RefObject, useEffect, useRef, useState } from "react";

export function useStuck(): { stuck: boolean; sentinelRef: RefObject<HTMLDivElement | null> } {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setStuck(!(entry?.isIntersecting ?? true)),
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return { stuck, sentinelRef };
}
